import { Fragment } from "react";
import Seo from "@/components/feature/Seo";
import PageHero from "@/components/feature/PageHero";
import Reveal from "@/components/base/Reveal";
import SectionHeading from "@/components/base/SectionHeading";
import ActionButton from "@/components/base/ActionButton";
import RetrofitEligibilityForm from "@/components/feature/RetrofitEligibilityForm";
import { journeySteps } from "@/mocks/navigation";
import { processSteps } from "@/mocks/process";

const compareRows = [
  { aspect: "What happens to the vehicle", replace: "A working vehicle is discarded or resold", retrofit: "A suitable existing vehicle is transformed" },
  { aspect: "Cost profile", replace: "Full cost of a new vehicle", retrofit: "Potentially lower transition cost, subject to assessment" },
  { aspect: "Vehicle history", replace: "Starts fresh with a new vehicle", retrofit: "Keeps a known, in-use vehicle in service" },
  { aspect: "Decision basis", replace: "Purchase decision", retrofit: "Technical assessment of the specific vehicle" },
];

const candidateFactors = [
  { icon: "ri-health-book-line", label: "Vehicle condition" },
  { icon: "ri-layout-masonry-line", label: "Vehicle architecture" },
  { icon: "ri-scales-3-line", label: "Weight and load" },
  { icon: "ri-route-line", label: "Usage and duty cycle" },
  { icon: "ri-ruler-2-line", label: "Space for integration" },
  { icon: "ri-shield-check-line", label: "Engineering assessment" },
];

export default function EvRetrofit() {
  return (
    <>
      <Seo
        title="EV Retrofit | ICE to EV Conversion by KAL LORDS ZERO21 DJT"
        description="What EV retrofit means, how the conversion process works and which vehicles can be considered for ICE to EV conversion. Subject to technical assessment."
        keywords="EV retrofit, ICE to EV conversion, EV conversion solutions, vehicle electrification"
        path="/ev-retrofit"
      />
      <PageHero
        eyebrow="EV Retrofit"
        breadcrumb="EV Retrofit"
        title="What EV Retrofit Means for Your Vehicle"
        description="EV retrofit converts a suitable existing vehicle into an electric vehicle. Instead of replacing the whole vehicle, the internal combustion drive is replaced and an electric powertrain, battery system and controls are integrated."
      >
        <div className="flex flex-col gap-3 sm:flex-row">
          <ActionButton to="/ev-retrofit#eligibility" variant="primary" size="md" icon="ri-flashlight-line">
            Check Retrofit Eligibility
          </ActionButton>
          <ActionButton to="/technology" variant="outlineLight" size="md">
            Explore the Technology
          </ActionButton>
        </div>
      </PageHero>

      <section className="w-full bg-background-50 py-16 md:py-24">
        <div className="mx-auto w-full max-w-container px-4 md:px-6">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-16">
            <Reveal>
              <SectionHeading
                eyebrow="The Core Idea"
                title="Don't Replace. Retrofit. Electrify."
                description="Replacing a serviceable vehicle is not the only route to electric mobility. Retrofit gives an existing vehicle a new drive system and a longer useful life."
              />
              <p className="mt-6 text-sm leading-relaxed text-foreground-600 md:text-base">
                The approach is simple in principle and engineering-led in practice: understand the
                vehicle, confirm technical feasibility, engineer the retrofit, install the electric
                powertrain, integrate the battery and control systems, then test and deploy. Every
                stage depends on the specific vehicle in front of us.
              </p>
              <div className="mt-8 rounded-lg border border-primary-200 bg-primary-50 p-5">
                <p className="text-sm leading-relaxed text-primary-900">
                  <i className="ri-information-line mr-2" />
                  Not every vehicle is a suitable candidate for retrofit. Final feasibility, cost,
                  range and configuration are subject to technical assessment.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.12}>
              <div className="overflow-hidden rounded-lg border border-background-200">
                <div className="h-64 w-full md:h-[440px]">
                  <img
                    src="https://readdy.ai/api/search-image?query=modern%20electric%20vehicle%20drive%20unit%20and%20control%20electronics%20displayed%20on%20a%20clean%20grey%20assembly%20bench%2C%20graphite%20and%20green%20accent%20lighting%2C%20premium%20engineering%20photography%2C%20sharp%20detail&width=1200&height=900&seq=klz-evretrofit-01&orientation=landscape"
                    alt="Electric vehicle drive unit and control electronics for retrofit"
                    title="ICE to EV conversion powertrain components"
                    className="h-full w-full object-cover object-top"
                  />
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="w-full bg-background-100 py-16 md:py-24">
        <div className="mx-auto w-full max-w-container px-4 md:px-6">
          <Reveal>
            <SectionHeading
              eyebrow="Retrofit vs Replace"
              title="A Different Way to Reach Electric Mobility"
              description="Retrofit and replacement are different paths. Which is right depends on the vehicle, the application and an engineering assessment."
            />
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-10 overflow-hidden rounded-lg border border-background-200 bg-background-50">
              <div className="grid grid-cols-1 md:grid-cols-3">
                <div className="border-b border-background-200 bg-background-200/40 px-5 py-4 font-label text-xs font-bold uppercase tracking-[0.14em] text-foreground-700 md:border-b-0 md:border-r">
                  Aspect
                </div>
                <div className="border-b border-background-200 bg-background-200/40 px-5 py-4 font-label text-xs font-bold uppercase tracking-[0.14em] text-foreground-700 md:border-b-0 md:border-r">
                  Replace
                </div>
                <div className="border-b border-background-200 bg-primary-100 px-5 py-4 font-label text-xs font-bold uppercase tracking-[0.14em] text-primary-900 md:border-b-0">
                  Retrofit
                </div>
                {compareRows.map((row) => (
                  <Fragment key={row.aspect}>
                    <div className="border-b border-background-200 px-5 py-4 text-sm font-semibold text-foreground-900 md:border-r">
                      {row.aspect}
                    </div>
                    <div className="border-b border-background-200 px-5 py-4 text-sm text-foreground-600 md:border-r">
                      {row.replace}
                    </div>
                    <div className="border-b border-background-200 px-5 py-4 text-sm text-foreground-700">
                      {row.retrofit}
                    </div>
                  </Fragment>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="w-full bg-background-50 py-16 md:py-24">
        <div className="mx-auto w-full max-w-container px-4 md:px-6">
          <Reveal>
            <SectionHeading
              eyebrow="Journey"
              title="The Path a Vehicle Takes"
              description="From an existing vehicle to sustainable mobility, the journey moves through assessment, engineering, electrification, connectivity and service."
            />
          </Reveal>
          <div className="mt-12 flex flex-wrap items-center gap-3">
            {journeySteps.map((step, index) => (
              <Reveal key={step} delay={index * 0.04}>
                <div className="flex items-center gap-3">
                  <span className="rounded-full border border-background-300 bg-background-100 px-4 py-2 font-label text-xs font-medium text-foreground-800">
                    {step}
                  </span>
                  {index < journeySteps.length - 1 ? (
                    <i className="ri-arrow-right-s-line text-primary-600" />
                  ) : null}
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1}>
            <div className="mt-16">
              <SectionHeading
                eyebrow="Candidates"
                title="What Decides If a Vehicle Can Be Retrofit"
                description="These factors shape feasibility. They are reviewed together — no single factor decides on its own."
              />
            </div>
          </Reveal>
          <div className="mt-10 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
            {candidateFactors.map((factor, index) => (
              <Reveal key={factor.label} delay={index * 0.05}>
                <div className="flex h-full flex-col items-center gap-3 rounded-lg border border-background-200 bg-background-100 p-5 text-center">
                  <span className="flex h-11 w-11 items-center justify-center rounded-md bg-background-50 text-primary-700">
                    <i className={`${factor.icon} text-xl`} />
                  </span>
                  <span className="font-label text-xs font-semibold text-foreground-800">
                    {factor.label}
                  </span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full bg-foreground-950 py-16 md:py-24">
        <div className="mx-auto w-full max-w-container px-4 md:px-6">
          <Reveal>
            <SectionHeading
              align="center"
              tone="dark"
              eyebrow="Process"
              title="Six Steps from Assessment to Deployment"
              description="Specifications and the final configuration are determined after technical assessment."
            />
          </Reveal>
          <div className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {processSteps.map((item, index) => (
              <Reveal key={item.step} delay={index * 0.06}>
                <article className="h-full rounded-lg border border-background-50/[0.12] bg-background-50/[0.04] p-6">
                  <div className="flex items-center justify-between">
                    <span className="font-heading text-3xl font-extrabold text-primary-400">{item.step}</span>
                    <span className="flex h-11 w-11 items-center justify-center rounded-md bg-background-50/10 text-accent-400">
                      <i className={`${item.icon} text-xl`} />
                    </span>
                  </div>
                  <h3 className="mt-5 text-base font-semibold text-background-50">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-background-300">{item.description}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section id="eligibility" className="w-full scroll-mt-24 bg-background-50 py-16 md:py-24">
        <div className="mx-auto w-full max-w-container px-4 md:px-6">
          <Reveal>
            <SectionHeading
              align="center"
              eyebrow="Retrofit Eligibility"
              title="Is Your Vehicle Ready for Electric?"
              description="Share your details and the team will review whether a retrofit can be considered for your vehicle."
            />
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mx-auto mt-10 max-w-4xl">
              <RetrofitEligibilityForm />
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}